import { diskR, worldToCam } from './camera';
import { livePos } from './occluders';

/**
 * Screen-space grid for the nest reveal solver.
 *
 * Every clearance test in `solve.js` (disk vs disk, hanging name vs disk) is
 * a camera-plane overlap. Dividing by view depth turns it into an angular
 * test: a node at angular position n/n.z touches a shallower disk at h/h.z
 * only when their angular distance is under
 *
 *     (R_hub + rimPad + 0.45·titleHoldPad) / h.z  +  R_node / n.z
 *
 * (`minXYClear`'s `keep`; the hang test is bounded by the same first term).
 * Bucketing live positions by angle lets each nested mission ask "who could
 * I be touching?" and get a handful of neighbours instead of the whole
 * graph. Pairs the grid skips would have produced a zero push anyway, so the
 * solve is unchanged — `revealBroadphase.test.js` checks that bit for bit.
 */
export const REVEAL_BROADPHASE = { enabled: true };

/** World pad on the shallower disk: rimPad cap (0.2) + 0.45 × titleHoldPad cap (0.34). */
const OTHER_PAD_WORLD = 0.2 + 0.45 * 0.34;
/** Float slack so a rounding hair never drops a real pair. */
const MARGIN = 1.02;
/**
 * Disk queries are grown by this fraction of a cell so the small graded
 * nudges the solver usually makes do not force a fresh query each time.
 */
const QUERY_SLACK = 0.25;
/** Below this view depth the clearance tests bail out, so nothing to bucket. */
const NEAR_Z = 0.25;
const KEY_OFF = 32768;
const KEY_MUL = 65536;

export function createRevealGrid(entries, camera, targets) {
  const n = entries.length;
  const ax = new Float64Array(n);
  const ay = new Float64Array(n);
  const inGrid = new Uint8Array(n);
  const cellKey = new Float64Array(n);
  /** Angular reach of each entry as the shallower disk in a pair. */
  const reach = new Float64Array(n);
  const cells = new Map();
  let maxOther = 0;
  let cell = 0.02;

  const keyOf = (ix, iy) => (ix + KEY_OFF) * KEY_MUL + (iy + KEY_OFF);

  function locate(i) {
    const e = entries[i];
    const c = worldToCam(livePos(e.mesh, e.tid, targets), camera);
    if (c.z < NEAR_Z) {
      inGrid[i] = 0;
      return;
    }
    ax[i] = c.x / c.z;
    ay[i] = c.y / c.z;
    inGrid[i] = 1;
    const r = ((diskR(e.mesh) + OTHER_PAD_WORLD) / c.z) * MARGIN;
    reach[i] = r;
    if (r > maxOther) maxOther = r;
  }

  function insert(i) {
    if (!inGrid[i]) return;
    const k = keyOf(Math.floor(ax[i] / cell), Math.floor(ay[i] / cell));
    let arr = cells.get(k);
    if (!arr) {
      arr = [];
      cells.set(k, arr);
    }
    arr.push(i);
    cellKey[i] = k;
  }

  function remove(i) {
    const arr = cells.get(cellKey[i]);
    if (!arr) return;
    const j = arr.indexOf(i);
    if (j >= 0) arr.splice(j, 1);
  }

  for (let i = 0; i < n; i += 1) locate(i);
  cell = Math.max(0.002, maxOther);
  for (let i = 0; i < n; i += 1) insert(i);

  /** Call after `targets` gains an entry for index `i` — its live seat moved. */
  function moved(i) {
    if (inGrid[i]) remove(i);
    locate(i);
    insert(i);
  }

  /**
   * Indices inside the angular rect grown by `maxOther`, then trimmed with
   * each candidate's own reach: a disk centre must sit within
   * `selfR + reach[i]` of (cx, cy); a box must contain the centre once grown
   * by `reach[i]`. Ascending order, so the sweep visits pairs as a full
   * scan would.
   */
  function queryRect(x0, x1, y0, y1, cx, cy, selfR, out) {
    out.length = 0;
    const ix0 = Math.floor((x0 - maxOther) / cell);
    const ix1 = Math.floor((x1 + maxOther) / cell);
    const iy0 = Math.floor((y0 - maxOther) / cell);
    const iy1 = Math.floor((y1 + maxOther) / cell);
    const disk = selfR >= 0;
    for (let ix = ix0; ix <= ix1; ix += 1) {
      for (let iy = iy0; iy <= iy1; iy += 1) {
        const arr = cells.get(keyOf(ix, iy));
        if (!arr) continue;
        for (let k = 0; k < arr.length; k += 1) {
          const i = arr[k];
          const g = reach[i];
          if (disk) {
            const dx = ax[i] - cx;
            const dy = ay[i] - cy;
            const lim = selfR + g;
            if (dx * dx + dy * dy > lim * lim) continue;
          } else if (ax[i] < x0 - g || ax[i] > x1 + g || ay[i] < y0 - g || ay[i] > y1 + g) {
            continue;
          }
          out.push(i);
        }
      }
    }
    if (out.length > 1) out.sort((a, b) => a - b);
    return out;
  }

  // Last disk query, so a small nudge can reuse its (slack-grown) result.
  let qx = 0;
  let qy = 0;
  let qr = 0;
  let qSlack = 0;

  /**
   * Neighbours that a disk of `radius` at world `pos` could overlap. The
   * result is grown by `maxOther` of slack; while the seat stays within
   * that slack, `stale()` is false and the same list still covers every
   * pair a fresh query would return.
   */
  function queryDisk(pos, radius, out) {
    const c = worldToCam(pos, camera);
    if (c.z < NEAR_Z) {
      out.length = 0;
      qSlack = -1;
      return out;
    }
    qx = c.x / c.z;
    qy = c.y / c.z;
    qr = (radius / c.z) * MARGIN;
    qSlack = maxOther * QUERY_SLACK;
    const r = qr + qSlack;
    return queryRect(qx - r, qx + r, qy - r, qy + r, qx, qy, r, out);
  }

  /** True once the seat has drifted past the slack of the last disk query. */
  function stale(pos, radius) {
    if (qSlack < 0) return true;
    const c = worldToCam(pos, camera);
    if (c.z < NEAR_Z) return true;
    const dx = c.x / c.z - qx;
    const dy = c.y / c.z - qy;
    const dr = Math.abs((radius / c.z) * MARGIN - qr);
    return Math.hypot(dx, dy) + dr > qSlack;
  }

  /** Neighbours a camera-plane box (at depth `box.tz`) could overlap. */
  function queryBox(box, out) {
    if (!box || box.tz < NEAR_Z) {
      out.length = 0;
      return out;
    }
    const inv = 1 / box.tz;
    return queryRect(box.left * inv, box.right * inv, box.bottom * inv, box.top * inv, 0, 0, -1, out);
  }

  return { moved, queryDisk, stale, queryBox };
}

/** World cap of `titlePad` — the only pad `minXYClearTitle` adds around a name. */
const TITLE_PAD_WORLD = 0.16;

/**
 * Static grid over camera-plane name boxes (`collectLabelOccluders`), in the
 * same angular space. A disk touches a name only when its angular centre is
 * within R/n.z of the name's angular box grown by titlePad/tz; a hanging
 * name touches one only when their angular boxes overlap. Boxes are stored
 * pre-grown so both queries are conservative.
 */
export function createBoxGrid(boxes) {
  const n = boxes.length;
  const l = new Float64Array(n);
  const r = new Float64Array(n);
  const b = new Float64Array(n);
  const t = new Float64Array(n);
  const stamp = new Uint32Array(n);
  let tick = 0;
  let cell = 0.02;
  let maxH = 0;
  for (let i = 0; i < n; i += 1) {
    const box = boxes[i];
    if (box.tz < NEAR_Z) {
      l[i] = 1;
      r[i] = 0;
      continue;
    }
    const inv = 1 / box.tz;
    const grow = TITLE_PAD_WORLD * inv * MARGIN;
    l[i] = box.left * inv - grow;
    r[i] = box.right * inv + grow;
    b[i] = box.bottom * inv - grow;
    t[i] = box.top * inv + grow;
    const h = t[i] - b[i];
    if (h > maxH) maxH = h;
  }
  cell = Math.max(0.002, maxH);
  const cells = new Map();
  const keyOf = (ix, iy) => (ix + KEY_OFF) * KEY_MUL + (iy + KEY_OFF);
  for (let i = 0; i < n; i += 1) {
    if (r[i] < l[i]) continue;
    const ix0 = Math.floor(l[i] / cell);
    const ix1 = Math.floor(r[i] / cell);
    const iy0 = Math.floor(b[i] / cell);
    const iy1 = Math.floor(t[i] / cell);
    for (let ix = ix0; ix <= ix1; ix += 1) {
      for (let iy = iy0; iy <= iy1; iy += 1) {
        const k = keyOf(ix, iy);
        let arr = cells.get(k);
        if (!arr) {
          arr = [];
          cells.set(k, arr);
        }
        arr.push(i);
      }
    }
  }

  function queryRect(x0, x1, y0, y1, out) {
    out.length = 0;
    if (!n) return out;
    tick += 1;
    const ix0 = Math.floor(x0 / cell);
    const ix1 = Math.floor(x1 / cell);
    const iy0 = Math.floor(y0 / cell);
    const iy1 = Math.floor(y1 / cell);
    for (let ix = ix0; ix <= ix1; ix += 1) {
      for (let iy = iy0; iy <= iy1; iy += 1) {
        const arr = cells.get(keyOf(ix, iy));
        if (!arr) continue;
        for (let k = 0; k < arr.length; k += 1) {
          const i = arr[k];
          if (stamp[i] === tick) continue;
          stamp[i] = tick;
          if (r[i] < x0 || l[i] > x1 || t[i] < y0 || b[i] > y1) continue;
          out.push(i);
        }
      }
    }
    if (out.length > 1) out.sort((a, c) => a - c);
    return out;
  }

  let qx = 0;
  let qy = 0;
  let qr = 0;
  let qSlack = -1;

  /** Names a disk of `radius` at world `pos` could touch, grown by slack. */
  function queryDisk(pos, radius, camera, out) {
    const c = worldToCam(pos, camera);
    if (c.z < NEAR_Z) {
      out.length = 0;
      qSlack = -1;
      return out;
    }
    qx = c.x / c.z;
    qy = c.y / c.z;
    qr = (radius / c.z) * MARGIN;
    qSlack = cell * QUERY_SLACK;
    const rr = qr + qSlack;
    return queryRect(qx - rr, qx + rr, qy - rr, qy + rr, out);
  }

  /** True once the seat has drifted past the slack of the last disk query. */
  function stale(pos, radius, camera) {
    if (qSlack < 0) return true;
    const c = worldToCam(pos, camera);
    if (c.z < NEAR_Z) return true;
    const dx = c.x / c.z - qx;
    const dy = c.y / c.z - qy;
    const dr = Math.abs((radius / c.z) * MARGIN - qr);
    return Math.hypot(dx, dy) + dr > qSlack;
  }

  /** Names a camera-plane box (at depth `box.tz`) could overlap. */
  function queryBox(box, out) {
    if (!box || box.tz < NEAR_Z) {
      out.length = 0;
      return out;
    }
    const inv = 1 / box.tz;
    return queryRect(box.left * inv, box.right * inv, box.bottom * inv, box.top * inv, out);
  }

  return { queryDisk, stale, queryBox };
}
